import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  TemplateRef,
  AfterViewInit,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TableComponent, TableColumn, PaginationConfig } from '../primeng';
import {
  FilterPanelComponent,
  FilterField,
} from '../filter-panel/filter-panel.component';
import { DateRange } from '../primeng/date-range-picker/date-range-picker.component';
import {
  ActivitiesApiService,
  ActivityResponse,
  Role,
} from '@angular-workspace/services';
import { FormatService } from '../../services/format.service';
import { FilterEnumsService } from '../../services/filter-enums.service';
import { ExportLoaderService } from '../../../services/export-loader.service';
import { WorkspaceContextService } from '../../../services/workspace-context.service';

export interface ActivityData {
  assetName: string;
  assetType: string;
  activity: string;
  accessedAt: string;
  /** Present when logType is COMPUTE (My Activities with tabs) */
  userName?: string;
  role?: string;
}

export interface ActivityFilters {
  type: string[];
  activity: string[];
  dateRange: DateRange | null;
}

export type ActivitiesLogType = 'ASSET' | 'COMPUTE' | 'USER_ACTION';

@Component({
  selector: 'app-activities',
  standalone: true,
  imports: [TableComponent, FilterPanelComponent],
  templateUrl: './activities.component.html',
  styleUrl: './activities.component.scss',
})
export class ActivitiesComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() showFilters: boolean = true;
  @Input() pageSize: number = 10;
  @Input() showPagination: boolean = true;
  @Input() showExportButton: boolean = true;
  @Input() disableSorting: boolean = false;
  /** When true, shows tabs: Assets, Compute, User action (and passes logType to API when loading) */
  @Input() showTabs: boolean = false;
  /** When true, show only Assets and User action tabs (no Compute tab). Use for platform-admin and Delegate Activities. */
  @Input() hideComputeTab: boolean = false;
  /** Optional `actorType` query param (e.g. `DELEGATE` on Delegate Activities). */
  @Input() actorType = '';
  /**
   * When true, use admin auditing endpoint (`.../auditing/admin/activity`).
   * When false, use consumer endpoint (`.../auditing/consumer/activity`).
   */
  @Input() useAdminAuditingApi: boolean = false;
  /** When true, the Usecase option is removed from the Asset Type filter. */
  @Input() hideUsecaseFilter: boolean = false;
  /** When true, uses the provider asset activity options list. */
  @Input() providerActivityFilter: boolean = false;
  /** When true, limits Assets tab to consumer-performed activities only. */
  @Input() consumerActivityFilter: boolean = false;
  /** When true, uses the reduced admin asset activity options (View, Delete). */
  @Input() adminActivityFilter: boolean = false;
  /** When true, adds a "User Name" column to the Assets / User Action table (sourced from the `orgName` API field). */
  @Input() showUserColumn: boolean = false;

  @ViewChild('activityCell', { static: false })
  activityCellTemplate!: TemplateRef<any>;

  /** Active tab when showTabs is true: ASSET | COMPUTE | USER_ACTION */
  activeLogType: ActivitiesLogType = 'ASSET';
  private readonly exportLoader = inject(ExportLoaderService);
  private readonly workspaceContext = inject(WorkspaceContextService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly activitiesApiService = inject(ActivitiesApiService);
  private readonly formatService = inject(FormatService);
  private readonly filterEnumsService = inject(FilterEnumsService);

  /**
   * Delegates do not see Compute on My Activities.
   * Org-admin delegates also do not see it on Org. User Activities.
   */
  get hideComputeTabForDelegate(): boolean {
    return (
      this.isDelegateMyActivitiesPage() ||
      this.isDelegateOrgUserActivitiesPage()
    );
  }

  private getCurrentPath(): string {
    return (this.router.url || '').split('?')[0].replace(/\/$/, '');
  }

  private isDelegateMyActivitiesPage(): boolean {
    if (!this.workspaceContext.isDelegateSession()) return false;
    return /^\/(consumer|provider|org-admin|admin)\/activities$/.test(
      this.getCurrentPath(),
    );
  }

  private isDelegateOrgUserActivitiesPage(): boolean {
    if (!this.workspaceContext.isDelegateSession()) return false;
    return (
      this.getCurrentPath() === '/org-admin/organisation-activities' &&
      this.workspaceContext.getActiveDelegatedPanelRole() === Role.ORG_ADMIN
    );
  }

  filterFields: FilterField[] = [];

  constructor() {
    this.initFilterFields();
  }

  private isDelegateActivitiesPanel(): boolean {
    return this.actorType.trim().toUpperCase() === 'DELEGATE';
  }

  /** Delegate My Activities, or the owner's Delegate Activities page. */
  private shouldRestrictDelegateActivityFilters(): boolean {
    return this.isDelegateMyActivitiesPage() || this.isDelegateActivitiesPanel();
  }

  private getAssetActivityFilterOptions(): string[] {
    let options: string[];
    if (this.adminActivityFilter) {
      options = this.filterEnumsService.getAdminAssetActivityOptions();
    } else if (this.providerActivityFilter) {
      options = this.filterEnumsService.getProviderActivityOptions();
    } else if (this.consumerActivityFilter) {
      options = this.filterEnumsService.getConsumerActivityOptions();
    } else {
      options = this.filterEnumsService.getActivityOptions();
    }
    return this.shouldRestrictDelegateActivityFilters()
      ? this.withDelegateAssetActivityOptions(options)
      : options;
  }

  /**
   * Drop Requested / Withdrawn / Rejected.
   * Keep Download Access Granted, except on platform-admin delegate My Activities.
   */
  private withDelegateAssetActivityOptions(options: string[]): string[] {
    const filtered = options.filter(
      (option) => !this.isDelegateRestrictedActivity(option),
    );
    if (this.isPlatformAdminDelegateMyActivities()) {
      return filtered.filter(
        (option) => !/download\s*access\s+granted/i.test(option),
      );
    }
    const hasGranted = filtered.some((option) =>
      /download\s*access\s+granted/i.test(option),
    );
    return hasGranted ? filtered : [...filtered, 'Download Access Granted'];
  }

  /** Platform Admin delegate viewing their own My Activities. */
  private isPlatformAdminDelegateMyActivities(): boolean {
    return (
      this.workspaceContext.isDelegateSession() &&
      this.getCurrentPath() === '/admin/activities'
    );
  }

  private isDelegateRestrictedActivity(option: string): boolean {
    const value = (option ?? '').trim();
    if (/^rating$/i.test(value)) return true;
    return /download\s*access/i.test(value) && !/granted/i.test(value);
  }

  private isConsumerAllowedAssetActivity(activity: string): boolean {
    if (
      this.shouldRestrictDelegateActivityFilters() &&
      this.isDelegateRestrictedActivity(activity)
    ) {
      return false;
    }
    if (this.showTabs && this.activeLogType !== 'ASSET') {
      return true;
    }
    if (!this.consumerActivityFilter) {
      return true;
    }
    const normalized = this.formatService.toPascalCase(activity);
    if (
      this.shouldRestrictDelegateActivityFilters() &&
      /download\s*access\s+granted/i.test(normalized)
    ) {
      return true;
    }
    return this.filterEnumsService
      .getConsumerActivityOptions()
      .some((option) => this.formatService.toPascalCase(option) === normalized);
  }

  private initFilterFields(): void {
    this.filterFields = [
      {
        key: 'type',
        label: 'Asset Type',
        type: 'multiselect',
        placeholder: 'Choose Type',
        options: this.hideUsecaseFilter
          ? this.filterEnumsService.getNonAdminAssetTypeOptions()
          : this.filterEnumsService.getAssetTypeOptions(),
        value: [],
      },
      {
        key: 'activity',
        label: 'Activity',
        type: 'multiselect',
        placeholder: 'Choose Activity',
        options: this.getAssetActivityFilterOptions(),
        value: [],
      },
      {
        key: 'dateRange',
        label: 'Date Range',
        type: 'dateRange',
        placeholder: 'Choose Date Range',
        value: null,
      },
    ];
  }

  filters: ActivityFilters = {
    type: [],
    activity: [],
    dateRange: null,
  };

  /** Columns for Assets / User action tabs */
  private assetColumns: TableColumn[] = [
    { key: 'assetName', label: 'Asset Name', sortable: true, width: '30%' },
    { key: 'assetType', label: 'Asset Type', sortable: false, width: '20%' },
    { key: 'activity', label: 'Activity', sortable: true, width: '25%' },
    {
      key: 'accessedAt',
      label: 'Accessed At',
      sortable: true,
      width: '25%',
      align: 'left',
    },
  ];

  /** Columns for Compute tab (My Activities with tabs): UserName, Role, Activity, Accessed At */
  private computeColumns: TableColumn[] = [
    { key: 'userName', label: 'User Name', sortable: true, width: '30%' },
    { key: 'role', label: 'Role', sortable: false, width: '25%' },
    { key: 'activity', label: 'Activity', sortable: true, width: '25%' },
    {
      key: 'accessedAt',
      label: 'Accessed At',
      sortable: true,
      width: '20%',
      align: 'left',
    },
  ];

  /** Compute tab activity filter options (alphabetical, Pascal case) */
  private get computeActivityOptions(): string[] {
    return this.filterEnumsService.getComputeActivityFilterOptions();
  }

  /** User action tab activity filter options (same as platform-admin User Activities). */
  private get userActionActivityOptions(): string[] {
    const options = ['Like', 'Dislike', 'Bookmark', 'Unbookmark', 'Rating'];
    return this.shouldRestrictDelegateActivityFilters()
      ? options.filter((option) => !this.isDelegateRestrictedActivity(option))
      : options;
  }

  /**
   * When no Activity filter is selected, repeat each value as `action` query params (matches admin API:
   * e.g. logType=COMPUTE → action=Create&action=Delete&…).
   */
  private getDefaultActionsForLogType(logType: ActivitiesLogType): string[] {
    if (logType === 'COMPUTE') {
      return [...this.computeActivityOptions];
    }
    if (logType === 'USER_ACTION') {
      return [...this.userActionActivityOptions];
    }
    return this.getAssetActivityFilterOptions().map((a) =>
      this.formatService.toPascalCase(a),
    );
  }

  get columns(): TableColumn[] {
    return this.showTabs && this.activeLogType === 'COMPUTE'
      ? this.computeColumns
      : this.assetColumns;
  }

  /** Cached filter fields for template (avoids new array every CD which can freeze UI when switching to Compute tab) */
  effectiveFilterFields: FilterField[] = [];

  /** Update effectiveFilterFields when tab or filter config changes; call after activeLogType/showTabs/filterFields change. */
  private updateEffectiveFilterFields(): void {
    if (!this.showTabs) {
      this.effectiveFilterFields = this.filterFields;
      return;
    }
    if (this.activeLogType === 'COMPUTE') {
      this.effectiveFilterFields = this.filterFields
        .filter((f) => f.key !== 'type')
        .map((f) => {
          if (f.key === 'activity') {
            return { ...f, options: [...this.computeActivityOptions] };
          }
          return f;
        });
      return;
    }
    if (this.activeLogType === 'USER_ACTION') {
      this.effectiveFilterFields = this.filterFields.map((f) => {
        if (f.key === 'activity') {
          return { ...f, options: [...this.userActionActivityOptions] };
        }
        return f;
      });
      return;
    }
    // ASSET tab: use default filter fields
    this.effectiveFilterFields = this.filterFields;
  }

  allActivitiesData: ActivityData[] = [];
  activitiesData: ActivityData[] = [];

  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true,
  };

  loading = false;
  emptyMessage = 'No Activities Found';
  private initialized = false;

  /** Current sort state for server-side sorting */
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  setLogType(logType: ActivitiesLogType): void {
    if (this.activeLogType === logType) return;
    this.activeLogType = logType;
    this.resetFiltersForTabChange();
    this.updateEffectiveFilterFields();
    this.pagination = { ...this.pagination, currentPage: 1 };
    this.loadActivities();
  }

  private resetFiltersForTabChange(): void {
    this.filters = { type: [], activity: [], dateRange: null };
    this.filterFields = this.filterFields.map((field) => {
      if (field.type === 'multiselect') {
        return { ...field, value: [] };
      }
      if (field.type === 'dateRange') {
        return { ...field, value: null };
      }
      return { ...field, value: '' };
    });
  }

  /**
   * Get activity class for styling based on activity type
   */
  getActivityClass(activity: string): string {
    const activityLower = activity?.toLowerCase() || '';

    if (activityLower.includes('view') || activityLower === 'view') {
      return 'activity-view';
    } else if (activityLower.includes('create') || activityLower === 'create') {
      return 'activity-create';
    } else if (activityLower.includes('update') || activityLower === 'update') {
      return 'activity-update';
    } else if (activityLower.includes('delete') || activityLower === 'delete') {
      return 'activity-delete';
    } else if (
      activityLower.includes('download') ||
      activityLower === 'download'
    ) {
      return 'activity-download';
    } else if (activityLower.includes('submit') || activityLower === 'submit') {
      return 'activity-submit';
    } else if (
      activityLower.includes('approve') ||
      activityLower === 'approve'
    ) {
      return 'activity-approve';
    } else if (activityLower.includes('reject') || activityLower === 'reject') {
      return 'activity-reject';
    } else if (activityLower === 'rating' || activityLower.includes('rating')) {
      return 'activity-rating';
    } else if (activityLower.includes('like') || activityLower === 'like') {
      return 'activity-like';
    } else if (
      activityLower.includes('bookmark') ||
      activityLower === 'bookmark'
    ) {
      return 'activity-bookmark';
    } else if (
      activityLower.includes('dislike') ||
      activityLower === 'dislike'
    ) {
      return 'activity-dislike';
    }

    return 'activity-default';
  }

  ngOnInit(): void {
    this.applyRouteOverrides();
    this.initFilterFields();
    this.updateEffectiveFilterFields();
    this.initializeAndLoad();
  }

  /** Delegate Activities route sets hideComputeTab + actorType=DELEGATE. */
  private applyRouteOverrides(): void {
    const data = this.route.snapshot.data ?? {};
    if (data['hideComputeTab'] === true) {
      this.hideComputeTab = true;
    }
    if (typeof data['actorType'] === 'string' && data['actorType'].trim()) {
      this.actorType = data['actorType'].trim();
    }
  }

  private initializeAndLoad(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    // Initialize columns with sorting based on disableSorting input
    this.updateColumnsSorting();

    // Initialize pagination with input values
    this.pagination = {
      enabled: this.showPagination,
      currentPage: 1,
      pageSize: this.pageSize,
      totalItems: 0,
      pageSizeOptions: [10, 25, 50, 100],
      serverSide: true,
    };

    this.loadActivities();
  }

  private updateColumnsSorting(): void {
    const sortable = !this.disableSorting;
    this.assetColumns = this.showUserColumn
      ? [
          { key: 'userName', label: 'User Name', sortable, width: '20%' },
          { key: 'assetName', label: 'Asset Name', sortable, width: '25%' },
          {
            key: 'assetType',
            label: 'Asset Type',
            sortable: false,
            width: '15%',
          },
          { key: 'activity', label: 'Activity', sortable, width: '20%' },
          {
            key: 'accessedAt',
            label: 'Accessed At',
            sortable,
            width: '20%',
            align: 'left',
          },
        ]
      : [
          { key: 'assetName', label: 'Asset Name', sortable, width: '30%' },
          {
            key: 'assetType',
            label: 'Asset Type',
            sortable: false,
            width: '20%',
          },
          { key: 'activity', label: 'Activity', sortable, width: '25%' },
          {
            key: 'accessedAt',
            label: 'Accessed At',
            sortable,
            width: '25%',
            align: 'left',
          },
        ];
    this.computeColumns = [
      { key: 'userName', label: 'User Name', sortable, width: '30%' },
      { key: 'role', label: 'Role', sortable: false, width: '25%' },
      { key: 'activity', label: 'Activity', sortable, width: '25%' },
      {
        key: 'accessedAt',
        label: 'Accessed At',
        sortable,
        width: '20%',
        align: 'left',
      },
    ];
  }

  ngAfterViewInit(): void {
    // Assign activity cell template to activity column in both column sets
    [this.assetColumns, this.computeColumns].forEach((cols) => {
      const activityCol = cols.find((col) => col.key === 'activity');
      if (activityCol && this.activityCellTemplate) {
        activityCol.cellTemplate = this.activityCellTemplate;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update columns sorting if disableSorting or showUserColumn changes
    if (changes['disableSorting'] || changes['showUserColumn']) {
      this.updateColumnsSorting();
    }
    // Re-build filter options when any activity-filter input is set by parent
    if (
      changes['hideUsecaseFilter'] ||
      changes['providerActivityFilter'] ||
      changes['consumerActivityFilter'] ||
      changes['adminActivityFilter']
    ) {
      this.initFilterFields();
      this.updateEffectiveFilterFields();
    }
    // Update filter fields when showTabs changes (e.g. Compute tab filter set)
    if (changes['showTabs']) {
      this.updateEffectiveFilterFields();
    }

    // Update pagination when pageSize or showPagination changes
    if (changes['pageSize'] || changes['showPagination']) {
      this.pagination = {
        ...this.pagination,
        pageSize: this.pageSize,
        enabled: this.showPagination,
      };

      // Check if any of the changed inputs are first changes
      const isFirstChange =
        (changes['pageSize'] && changes['pageSize'].firstChange) ||
        (changes['showPagination'] && changes['showPagination'].firstChange) ||
        (changes['showFilters'] && changes['showFilters'].firstChange);

      if (isFirstChange && !this.initialized) {
        // This is the first change (fresh component), so initialize here
        // This handles the case where ngOnChanges is called before ngOnInit
        setTimeout(() => {
          if (!this.initialized) {
            this.initializeAndLoad();
          }
        }, 0);
      } else if (!isFirstChange) {
        // Reload activities with new page size if not initial change (component reused)
        this.pagination = { ...this.pagination, currentPage: 1 };
        this.loadActivities();
      }
    }
  }

  /**
   * Map filter display values to API format
   * @param filterValue Display value (e.g., "Dataset")
   * @returns API format value (e.g., "DATABANK")
   */
  private mapAssetTypeToApiFormat(filterValue: string): string {
    return this.filterEnumsService.mapAssetTypeToApi(filterValue);
  }

  /**
   * Normalise date range to { startDate, endDate } for formatDateRangeForApi (handles start/end or startDate/endDate).
   * Activity API receives: time=YYYY-MM-DDTHH:mm:ss&endtime=YYYY-MM-DDTHH:mm:ss&timerel=between
   */
  private getDateRangeForApi(
    dateRange: any,
  ): { startDate: string; endDate: string } | undefined {
    if (!dateRange) return undefined;
    const start = dateRange.startDate ?? dateRange.start;
    const end = dateRange.endDate ?? dateRange.end;
    if (!start || !end) return undefined;
    return this.formatService.formatDateRangeForApi({
      startDate: start,
      endDate: end,
    });
  }

  /**
   * Get activity value for API (action param). Handles string or option object – returns PascalCase (e.g. Approve, Create).
   */
  private getActivityActionValue(item: any): string | null {
    if (item == null) return null;
    if (typeof item === 'string' && item.trim())
      return this.formatService.toPascalCase(item.trim());
    if (typeof item === 'object' && item !== null) {
      const raw =
        (item as { value?: string; label?: string }).value ??
        (item as { value?: string; label?: string }).label;
      if (typeof raw === 'string' && raw.trim())
        return this.formatService.toPascalCase(raw.trim());
    }
    return null;
  }

  async loadActivities(): Promise<void> {
    this.loading = true;
    try {
      // Map filter values to API format
      const entityTypes =
        this.filters.type && this.filters.type.length > 0
          ? this.filters.type.map((type) => this.mapAssetTypeToApiFormat(type))
          : undefined;

      // Map activity filter values to action param (PascalCase: Approve → &action=Approve, Create → &action=Create)
      let operations =
        this.filters.activity && this.filters.activity.length > 0
          ? this.filters.activity
              .map((activity) => this.getActivityActionValue(activity))
              .filter((v): v is string => v != null && v !== '')
          : undefined;

      if (this.showTabs && (!operations || operations.length === 0)) {
        operations = this.getDefaultActionsForLogType(this.activeLogType);
      }

      // Date range for API: time=YYYY-MM-DDTHH:mm:ss&endtime=YYYY-MM-DDTHH:mm:ss&timerel=between
      const formattedDateRange = this.getDateRangeForApi(
        this.filters.dateRange,
      );
      const startDate = formattedDateRange?.startDate;
      const endDate = formattedDateRange?.endDate;

      // Build sort query param (fieldName:asc or fieldName:desc)
      // Map UI column keys to API field names (Compute tab uses userName, role)
      const columnToApiField: Record<string, string> =
        this.showTabs && this.activeLogType === 'COMPUTE'
          ? {
              activity: 'action',
              accessedAt: 'createdAt',
              userName: 'userName',
              role: 'role',
            }
          : { activity: 'action', accessedAt: 'createdAt' };
      const apiSortField = this.sortColumn
        ? (columnToApiField[this.sortColumn] ?? this.sortColumn)
        : null;
      const sortParam = apiSortField
        ? `${apiSortField}:${this.sortDirection}`
        : undefined;

      const logTypeParam = this.showTabs ? this.activeLogType : 'ASSET';
      // My Activities shows the signed-in user's own actions. Pages that already set an actor keep it.
      const actorTypeParam =
        this.actorType.trim() ||
        (this.useAdminAuditingApi ? undefined : 'SELF');

      const fetchPage = () =>
        this.useAdminAuditingApi
          ? this.activitiesApiService.getAllPlatformUserActivities(
              this.pagination.currentPage,
              this.pageSize,
              entityTypes,
              operations,
              startDate,
              endDate,
              sortParam,
              logTypeParam,
              actorTypeParam,
            )
          : this.activitiesApiService.getUserActivities(
              this.pagination.currentPage,
              this.pageSize,
              entityTypes,
              operations,
              startDate,
              endDate,
              sortParam,
              logTypeParam,
              actorTypeParam,
            );

      // Use this.pageSize from input instead of pagination.pageSize
      // Retry once on 401 to handle token race on first load (token not yet in headers)
      let response: ActivityResponse;
      try {
        response = await fetchPage();
      } catch (err: any) {
        if (err?.status === 401) {
          await new Promise((r) => setTimeout(r, 500));
          response = await fetchPage();
        } else {
          throw err;
        }
      }

      const isComputeTab = this.showTabs && this.activeLogType === 'COMPUTE';
      const mapComputeItem = (item: any): ActivityData => ({
        assetName: '',
        assetType: '',
        userName:
          item.userName || item.user_name || item.userId || 'Unknown User',
        role: item.role
          ? item.role.charAt(0).toUpperCase() + item.role.slice(1).toLowerCase()
          : '',
        activity: this.formatService.toPascalCase(
          item.action || item.activity || '',
        ),
        accessedAt: this.formatService.formatDate(
          item.createdAt ||
            item.accessedAt ||
            item.accessed_at ||
            item.timestamp ||
            '',
        ),
      });
      const mapAssetItem = (item: any): ActivityData => {
        const rawAssetType =
          item.entityType ||
          item.assetType ||
          item.asset_type ||
          item.type ||
          '';
        let formattedAssetType = rawAssetType
          ? this.formatService.formatAssetType(rawAssetType)
          : '';
        if (
          formattedAssetType &&
          formattedAssetType.toUpperCase().trim() === 'DATABANK'
        ) {
          formattedAssetType = 'Dataset';
        }
        return {
          assetName:
            item.entityName ||
            item.assetName ||
            item.asset_name ||
            item.name ||
            '',
          assetType: formattedAssetType,
          userName:
            item.userName ||
            item.user_name ||
            item.orgName ||
            item.userId ||
            '',
          activity: this.formatService.toPascalCase(
            item.action || item.activity || '',
          ),
          accessedAt: this.formatService.formatDate(
            item.createdAt ||
              item.accessedAt ||
              item.accessed_at ||
              item.timestamp ||
              '',
          ),
        };
      };
      const mapItem = (item: any): ActivityData | null => {
        const mapped = isComputeTab ? mapComputeItem(item) : mapAssetItem(item);
        return this.isConsumerAllowedAssetActivity(mapped.activity)
          ? mapped
          : null;
      };

      let mappedData: ActivityData[] = [];
      if (response && response['result'] && Array.isArray(response['result'])) {
        const dataArray = response['result'];
        mappedData = dataArray
          .map((item: any) => mapItem(item))
          .filter((item): item is ActivityData => item != null);

        // Update pagination from paginationInfo
        if (
          response['paginationInfo'] &&
          response['paginationInfo']['totalCount'] !== undefined
        ) {
          this.pagination = {
            ...this.pagination,
            totalItems: response['paginationInfo']['totalCount'],
          };
        }
      } else if (
        response &&
        response['data'] &&
        Array.isArray(response['data'])
      ) {
        // Fallback: Handle legacy response structure with 'data' property
        const dataArray = response['data'];
        mappedData = dataArray
          .map((item: any) => mapItem(item))
          .filter((item): item is ActivityData => item != null);

        if (response['total'] !== undefined) {
          this.pagination = {
            ...this.pagination,
            totalItems: response['total'],
          };
        } else if (response['count'] !== undefined) {
          this.pagination = {
            ...this.pagination,
            totalItems: response['count'],
          };
        }
      } else if (Array.isArray(response)) {
        // Fallback: Handle direct array response
        mappedData = response
          .map((item: any) => mapItem(item))
          .filter((item): item is ActivityData => item != null);
        this.pagination = { ...this.pagination, totalItems: mappedData.length };
      } else {
        // 204 No Content or empty response – no result/data/array; ensure pagination shows zero items
        this.pagination = { ...this.pagination, totalItems: 0 };
      }

      this.activitiesData = mappedData;
      this.allActivitiesData = [...mappedData];

      if (mappedData.length === 0) {
        this.emptyMessage = 'No Activities Found';
      }
    } catch (error) {
      console.error('Error loading activities:', error);
      this.activitiesData = [];
      this.allActivitiesData = [];
      this.pagination = { ...this.pagination, totalItems: 0 };
      this.emptyMessage = 'No Activities Found';
    } finally {
      this.loading = false;
    }
  }

  onPageChange(page: number): void {
    this.pagination = { ...this.pagination, currentPage: page };
    this.loadActivities();
  }

  onPageSizeChange(pageSize: number): void {
    // Only update if pagination is enabled (not in dashboard mode)
    if (this.showPagination) {
      this.pageSize = pageSize;
      this.pagination = {
        ...this.pagination,
        pageSize: pageSize,
        currentPage: 1,
      };
      this.loadActivities();
    }
  }

  onSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    this.sortColumn = event.column;
    this.sortDirection = event.direction;
    // Reset to first page when sort changes
    this.pagination = { ...this.pagination, currentPage: 1 };
    this.loadActivities();
  }

  onFilterChange(filterValues: Record<string, any>): void {
    this.filters = {
      type: Array.isArray(filterValues['type']) ? filterValues['type'] : [],
      activity: Array.isArray(filterValues['activity'])
        ? filterValues['activity']
        : [],
      dateRange: filterValues['dateRange'] || null,
    };
    // Normalise activity to strings for API (action=Approve, action=Create etc.)
    this.filters.activity = this.filters.activity
      .map((a: any) => this.getActivityActionValue(a))
      .filter((v): v is string => v != null && v !== '');
    this.filterFields = this.filterFields.map((field) => {
      if (field.key === 'type') {
        return { ...field, value: [...this.filters.type] };
      }
      if (field.key === 'activity') {
        return { ...field, value: [...this.filters.activity] };
      }
      if (field.key === 'dateRange') {
        return { ...field, value: this.filters.dateRange };
      }
      return field;
    });
    // Reset to first page when filters change
    this.pagination = { ...this.pagination, currentPage: 1 };
    // Reload activities with new filters
    this.loadActivities();
  }

  onClearFilters(): void {
    this.filters = { type: [], activity: [], dateRange: null };
    // Reset filter field values
    this.filterFields.forEach((field) => {
      if (field.type === 'multiselect') {
        field.value = [];
      } else if (field.type === 'dateRange') {
        field.value = null;
      } else {
        field.value = '';
      }
    });
    // Reset to first page and reload activities
    this.pagination = { ...this.pagination, currentPage: 1 };
    this.loadActivities();
  }

  async onExport(): Promise<void> {
    try {
      await this.exportLoader.run(async () => {
        if (this.useAdminAuditingApi) {
          await this.exportAdminActivityReport();
        } else {
          const response =
            await this.activitiesApiService.getConsumerActivityReport();
          if (!response) return;
          this.triggerBlobDownload(response, 'activity-report.csv');
        }
      });
    } catch (error) {
      console.error('Export failed:', error);
    }
  }

  /** Admin report may return CSV text or JSON rows — same handling as TANUH. */
  private async exportAdminActivityReport(): Promise<void> {
    const blob = await this.activitiesApiService.getAdminActivityReport(
      this.activeLogType,
    );
    const text = await blob.text();
    const trimmed = text.trim();
    let csvContent: string;

    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      let parsed: unknown;
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        this.triggerBlobDownload(
          new Blob([text], { type: 'text/csv' }),
          'activity-report.csv',
        );
        return;
      }
      const rows = this.extractRowsFromReport(parsed);
      if (!rows.length) return;
      csvContent = this.jsonRowsToCsv(rows);
    } else {
      csvContent = text;
    }

    this.triggerBlobDownload(
      new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }),
      'activity-report.csv',
    );
  }

  private extractRowsFromReport(parsed: unknown): Record<string, unknown>[] {
    if (Array.isArray(parsed)) {
      return parsed.filter((r) => r && typeof r === 'object') as Record<
        string,
        unknown
      >[];
    }
    if (parsed && typeof parsed === 'object') {
      const o = parsed as Record<string, unknown>;
      for (const c of [o['result'], o['data'], o['records']]) {
        if (Array.isArray(c)) {
          return c.filter((r) => r && typeof r === 'object') as Record<
            string,
            unknown
          >[];
        }
      }
    }
    return [];
  }

  private jsonRowsToCsv(rows: Record<string, unknown>[]): string {
    const keySet = new Set<string>();
    rows.forEach((r) => Object.keys(r).forEach((k) => keySet.add(k)));
    const keys = [...keySet];
    const escape = (v: unknown): string => {
      if (v == null) return '';
      const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [keys.join(',')];
    rows.forEach((row) => {
      lines.push(keys.map((k) => escape(row[k])).join(','));
    });
    return lines.join('\n');
  }

  private triggerBlobDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}
